import { useTranslation } from 'react-i18next'
import { Shield, ShieldCheck, KeyRound } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Badge } from '@/shared/components/ui/badge'
import { useAuth } from '@/shared/lib/auth/auth-context'
import { useMyRoles } from '@/features/roles/hooks/use-my-roles'
import { MyRolesSection } from '@/features/roles/components/my-roles-section'

interface UserAccessDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Role IDs only, for a caller nothing can name the roles for (UC-084.9 E2). */
function RoleIdBadges({ roleIds }: { roleIds: string[] }) {
  const { t } = useTranslation()
  if (roleIds.length === 0) return <p className="text-xs text-muted-foreground">{t('userAccess.noRoles')}</p>
  return (
    <>
      <div className="flex flex-wrap gap-1.5">
        {roleIds.map((roleId) => (
          <Badge key={roleId} variant="outline" className="gap-1 font-mono text-[11px]">
            <KeyRound className="h-3 w-3" />#{roleId}
          </Badge>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">{t('userAccess.rolesHint')}</p>
    </>
  )
}

export function UserAccessDialog({ open, onOpenChange }: UserAccessDialogProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const myRoles = useMyRoles(open)

  const tenantName = user?.tenants.find((tenant) => tenant.id === user.organizationId)?.name

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('userAccess.title')}</DialogTitle>
          <DialogDescription>{t('userAccess.description')}</DialogDescription>
        </DialogHeader>

        {!user ? (
          <p className="text-sm text-muted-foreground">{t('userAccess.notAuthenticated')}</p>
        ) : (
          <div className="space-y-4 text-sm">
            {/* Identity */}
            <div className="space-y-1">
              <div className="font-medium">{user.email}</div>
              {tenantName && (
                <div className="text-xs text-muted-foreground">
                  {t('userAccess.tenant')}: {tenantName}
                </div>
              )}
            </div>

            {/* Flags */}
            <div className="flex gap-2">
              {user.isSuperAdmin && (
                <Badge variant="default" className="gap-1">
                  <ShieldCheck className="h-3 w-3" />
                  {t('userAccess.superAdmin')}
                </Badge>
              )}
              {user.isAdmin && (
                <Badge variant="secondary" className="gap-1">
                  <Shield className="h-3 w-3" />
                  {t('userAccess.admin')}
                </Badge>
              )}
              {!user.isAdmin && !user.isSuperAdmin && (
                <span className="text-xs text-muted-foreground">
                  {t('userAccess.standardUser')}
                </span>
              )}
            </div>

            {/* Roles, each with what it grants (UC-084.9) */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('userAccess.roles')} ({user.roles.length})
              </h3>
              {myRoles.roles === null
                ? <RoleIdBadges roleIds={user.roles} />
                : <MyRolesSection roles={myRoles.roles} isLoading={myRoles.isLoading} />}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
